import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { Copy, User, Bot } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { format } from 'date-fns'
import type { InteractionMessage } from '../../../types'

interface MessageBubbleProps {
  message: InteractionMessage
}

export function MessageBubble({ message }: MessageBubbleProps) {
  const { toast } = useToast()
  const isUser = message.message_type === 'user'
  const createdDate = new Date(message.created_at)

  const handleCopy = () => {
    navigator.clipboard.writeText(message.content)
    toast({
      title: 'Copied!',
      description: 'Message copied to clipboard',
    })
  }

  return (
    <div className={`flex items-start gap-3 ${isUser ? '' : 'flex-row-reverse'}`}>
      {/* Avatar */}
      <Avatar className="w-10 h-10">
        <AvatarFallback className={isUser ? 'bg-secondary' : 'bg-primary text-primary-foreground'}>
          {isUser ? <User className="w-5 h-5" /> : <Bot className="w-5 h-5" />}
        </AvatarFallback>
      </Avatar>

      {/* Message Content */}
      <div className={`flex-1 ${isUser ? 'items-start' : 'items-end'} flex flex-col`}>
        {/* Header */}
        <div className={`flex items-center gap-2 mb-1 ${isUser ? '' : 'flex-row-reverse'}`}>
          <span className="text-sm font-medium">
            {isUser ? 'User' : 'Assistant'}
          </span>
          <time className="text-xs text-muted-foreground">
            {format(createdDate, 'h:mm a')}
          </time>
        </div>

        {/* Bubble */}
        <div
          className={`rounded-lg p-3 max-w-[80%] relative ${
            isUser
              ? 'bg-secondary text-secondary-foreground'
              : 'bg-primary text-primary-foreground'
          }`}
        >
          <div className="whitespace-pre-wrap break-words">{message.content}</div>
          
          {/* Copy button for assistant messages */}
          {!isUser && (
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-6 w-6 hover:bg-primary-foreground/20"
              onClick={handleCopy}
            >
              <Copy className="h-3 w-3" />
            </Button>
          )}
        </div>

        {/* Footer with date */}
        <span className="text-xs text-muted-foreground mt-1">
          {format(createdDate, 'MMMM d, yyyy')}
        </span>

        {/* Sources */}
        {message.has_sources && message.sources.length > 0 && (
          <div className="mt-2 p-3 bg-muted rounded-lg text-sm max-w-[80%]">
            <div className="font-semibold mb-1">Sources:</div>
            <ul className="list-disc list-inside space-y-1">
              {message.sources.map((source, idx) => (
                <li key={idx} className="text-muted-foreground">
                  {source.title || source.document_id}
                  {source.page && ` - Page ${source.page}`}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Performance Metrics */}
        {message.metrics && (
          <div className="mt-2 p-3 bg-muted rounded-lg text-sm max-w-[80%]">
            <div className="font-semibold mb-1">Performance Metrics:</div>
            <div className="grid grid-cols-2 gap-2 text-muted-foreground">
              <div>Total time: {message.metrics.total_ms.toFixed(1)} ms</div>
              <div>Tokens generated: {message.metrics.generated_tokens} (approx)</div>
              <div>Token speed: {message.metrics.token_speed.toFixed(1)}/s</div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
